import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  AutopayLogDto,
  CustomerBillingDto,
  DeclinedPaymentDto,
  OcsPurchaseDto,
  OcsPurchasesDto,
  TaxExemptKind,
  TaxExemptionDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { userLabel } from './parts';

/**
 * Sections of Billing and Invoice added from the walk of 2026-10-20: Autopay Log (PC678),
 * Declined Payment (PC932), OCS Purchase (PC465), Apply Coupon (PC1195) and the Tax Exemption
 * Setting dialog (PC459). The reference showed their names and empty answers only; the columns
 * are this project's.
 */

const path = (customerId: string, rest: string) => `/billing/customers/${customerId}/${rest}`;
const NO_RECORD = 'No record found';

const AUTOPAY_COLUMNS: Column<AutopayLogDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDateTime(r.at) },
  { key: 'event', header: 'Event', render: (r) => r.event },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (r) => (r.amountCents === null ? '' : formatCents(r.amountCents)),
  },
  { key: 'invoiceNo', header: 'Invoice No', nowrap: true, render: (r) => r.invoiceNo ?? '' },
  { key: 'reason', header: 'Reason', render: (r) => r.reason },
  { key: 'by', header: 'By', render: (r) => userLabel(r.by) || 'System' },
];

export function AutopayLogList({ customerId }: { customerId: string }) {
  const log = useQuery({
    queryKey: ['billing', 'customer', customerId, 'autopay-log'],
    queryFn: () => api.get<{ data: AutopayLogDto[] }>(path(customerId, 'autopay-log')),
    select: (r) => r.data,
  });
  return (
    <DataTable
      label="Autopay Log"
      columns={AUTOPAY_COLUMNS}
      rows={log.data}
      rowKey={(r) => r.id}
      loading={log.isFetching}
      error={log.error}
      onRetry={() => void log.refetch()}
      emptyText={NO_RECORD}
    />
  );
}

const DECLINED_COLUMNS: Column<DeclinedPaymentDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDateTime(r.at) },
  { key: 'for', header: 'Payment For', render: (r) => r.paymentFor },
  { key: 'method', header: 'Payment Method', render: (r) => r.method },
  { key: 'reason', header: 'Reason', render: (r) => r.reason },
];

export function DeclinedPaymentList({ customerId }: { customerId: string }) {
  const list = useQuery({
    queryKey: ['billing', 'customer', customerId, 'declined'],
    queryFn: () => api.get<{ data: DeclinedPaymentDto[] }>(path(customerId, 'declined-payments')),
    select: (r) => r.data,
  });
  return (
    <DataTable
      label="Declined Payment"
      columns={DECLINED_COLUMNS}
      rows={list.data}
      rowKey={(r) => r.id}
      loading={list.isFetching}
      error={list.error}
      onRetry={() => void list.refetch()}
      emptyText={NO_RECORD}
    />
  );
}

const OCS_COLUMNS: Column<OcsPurchaseDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDate(r.at) },
  { key: 'product', header: 'Product', render: (r) => r.product },
  { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatCents(r.amountCents) },
  { key: 'status', header: 'Status', render: (r) => r.status },
];

/** OCS Purchase: the carrier's answer, or the reference's "Carrier not specified.". */
export function OcsPurchaseList({ customerId }: { customerId: string }) {
  const list = useQuery({
    queryKey: ['billing', 'customer', customerId, 'ocs'],
    queryFn: () => api.get<OcsPurchasesDto>(path(customerId, 'ocs-purchases')),
  });
  if (list.data?.message) return <Typography>{list.data.message}</Typography>;
  return (
    <DataTable
      label="OCS Purchase"
      columns={OCS_COLUMNS}
      rows={list.data?.data}
      rowKey={(r) => `${r.at}-${r.product}`}
      loading={list.isFetching}
      error={list.error}
      onRetry={() => void list.refetch()}
      emptyText={NO_RECORD}
    />
  );
}

/**
 * Apply Coupon: a coupon code applied to one unpaid invoice. Assumption: the reference section
 * was seen loading only; the terms are checked as on Plan Renew.
 */
export function ApplyCouponPanel({
  customerId,
  billing,
}: {
  customerId: string;
  billing: CustomerBillingDto;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const unpaid = billing.invoices.filter((i) => i.status === 'Unpaid');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [code, setCode] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const apply = useMutation({
    mutationFn: (body: { invoiceNo: number; couponCode: string }) =>
      api.post<{ message: string }>(path(customerId, 'apply-coupon'), body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!invoiceNo) next.invoiceNo = 'Select Invoice';
    if (!code.trim()) next.couponCode = 'Enter Coupon Code';
    setProblems(next);
    if (Object.keys(next).length) return;
    try {
      const res = await apply.mutateAsync({
        invoiceNo: Number(invoiceNo.replace(/\D/g, '')),
        couponCode: code,
      });
      notify.success(res.message);
      setCode('');
      setInvoiceNo('');
    } catch (err) {
      if (err instanceof ApiError && err.details.length)
        setProblems(Object.fromEntries(err.details.map((d) => [d.path, d.message])));
      else notify.error(err);
    }
  };
  if (!unpaid.length) return <Typography>This customer has no unpaid invoice.</Typography>;
  return (
    <Box
      component="form"
      noValidate
      aria-label="Apply Coupon"
      onSubmit={(e) => void submit(e)}
      sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}
    >
      <TextField
        select
        label="Invoice"
        value={invoiceNo}
        onChange={(e) => setInvoiceNo(e.target.value)}
        error={Boolean(problems.invoiceNo)}
        helperText={problems.invoiceNo}
        sx={{ minWidth: 280 }}
      >
        {unpaid.map((i) => (
          <MenuItem key={i.id} value={i.invoiceNo}>
            {i.invoiceNo} · {i.invoiceType} · {formatCents(i.totalDueCents)} due
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Coupon Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        error={Boolean(problems.couponCode)}
        helperText={problems.couponCode}
        slotProps={{ htmlInput: { maxLength: 40 } }}
      />
      <Button type="submit" variant="contained" disabled={apply.isPending} sx={{ mt: 1 }}>
        Apply
      </Button>
    </Box>
  );
}

/**
 * "Tax Exemption Setting" (PC459): the kinds of charge with the tax rate of the customer's
 * state; ticking one exempts the customer from tax on it. Submit saves.
 */
export function TaxExemptionDialog({
  customerId,
  open,
  onClose,
}: {
  customerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const key = ['billing', 'customer', customerId, 'tax-exemptions'];
  const screen = useQuery({
    queryKey: key,
    queryFn: () => api.get<TaxExemptionDto>(path(customerId, 'tax-exemptions')),
    enabled: open,
  });
  const [chosen, setChosen] = useState<TaxExemptKind[]>([]);
  useEffect(() => {
    if (screen.data) setChosen(screen.data.items.filter((i) => i.exempt).map((i) => i.kind));
  }, [screen.data]);
  const save = useMutation({
    mutationFn: (kinds: TaxExemptKind[]) =>
      api.put<TaxExemptionDto>(path(customerId, 'tax-exemptions'), { kinds }),
    onSuccess: (next) => queryClient.setQueryData(key, next),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync(chosen);
      notify.success('Tax exemption saved.');
      onClose();
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <FormDialog
      open={open}
      title="Tax Exemption Setting"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
    >
      {screen.error ? <Alert severity="error">Could not load the tax settings.</Alert> : null}
      {screen.data ? (
        <Box>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            Tax rates of {screen.data.state || 'the service state'}. A ticked charge is not taxed
            for this customer.
          </Typography>
          {screen.data.items.map((item) => (
            <FormControlLabel
              key={item.kind}
              sx={{ display: 'flex' }}
              label={`${item.label} (${item.ratePercent}%)`}
              control={
                <Checkbox
                  size="small"
                  checked={chosen.includes(item.kind)}
                  onChange={(e) =>
                    setChosen((list) =>
                      e.target.checked ? [...list, item.kind] : list.filter((k) => k !== item.kind),
                    )
                  }
                />
              }
            />
          ))}
        </Box>
      ) : null}
    </FormDialog>
  );
}
